import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { matchClientDropboxFolder } from "@/lib/document-folder-matching";

export interface Client {
  id: string;
  name: string; // Company/organization name (e.g., "Napa Valley Estates")
  code: string | null; // Short client code (e.g., "PL") for lot ID coding
  first_name: string | null; // Primary contact: first name
  last_name: string | null; // Primary contact: last name
  contact_email: string | null; // Primary contact: email
  phone: string | null; // Primary contact: phone
  is_active: boolean; // Soft delete flag - false means archived
  org_id: string;
  created_at: string;
  updated_at: string;
  vintrace_account: any;
}

// Dropbox destination folder for a client at a specific facility (a client
// can have folders at both, e.g. "GWH - Pisoni" and "RP - Pisoni").
export interface ClientFacilityFolder {
  id: string;
  client_id: string;
  facility_id: string;
  dropbox_folder: string;
  created_at: string;
  updated_at: string;
}

export interface CreateClientInput {
  name: string; // Company/organization name
  code: string; // Short client code (e.g., "PL") -- lot ID prefix, routed filenames, packing-slip matching
  first_name?: string; // Primary contact: first name
  last_name?: string; // Primary contact: last name
  contact_email?: string; // Primary contact: email
  phone?: string; // Primary contact: phone
}

// Client codes are uppercase with no spaces ("af" / " AF " -> "AF").
export const normalizeClientCode = (code: string) => code.replace(/\s+/g, '').toUpperCase();

// client.code is unique per org (client_org_code_unique) -- turn the raw
// Postgres unique-violation into something a person can act on.
function clientErrorMessage(error: Error & { code?: string }, code?: string): string {
  if (error.code === '23505' && code) {
    return `Client code "${code}" is already used by another client (possibly an archived one).`;
  }
  return error.message;
}

export interface UpdateClientInput {
  first_name?: string;
  last_name?: string;
  name?: string;
  code?: string;
  contact_email?: string;
  phone?: string;
}

/**
 * Resolves the current user's org_id. Shared by mutations that need to stamp
 * org_id on an insert (Supabase RLS/grants require it explicitly, it isn't
 * inferred server-side).
 */
async function getCurrentUserOrgId(): Promise<string> {
  const { data: userData, error: userError } = await supabase
    .from("user_account")
    .select("org_id")
    .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
    .single();

  if (userError || !userData) throw new Error("Failed to get user organization");
  return userData.org_id;
}

export function useClients(searchTerm?: string) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["clients", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("client")
        .select("*")
        .neq("is_active", false) // Only show active clients (includes true and null for backwards compatibility)
        .order("name", { ascending: true });

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Client[];
    },
    // Only run query when auth is ready to prevent RLS returning empty results
    enabled: isAuthenticated && !authLoading,
  });
}

export function useClientStats(clientId: string) {
  return useQuery({
    queryKey: ["client-stats", clientId],
    queryFn: async () => {
      const lotsResult = await supabase
        .from("wine_lot")
        .select("id, current_volume_l, varietal")
        .eq("client_id", clientId);

      if (lotsResult.error) throw lotsResult.error;

      const totalVolume = lotsResult.data.reduce(
        (sum, lot) => sum + (Number(lot.current_volume_l) || 0),
        0
      );

      // Collect distinct varietals from lots
      const varietals = [...new Set(
        lotsResult.data
          .map(lot => (lot as any).varietal as string | null)
          .filter((v): v is string => !!v)
      )].sort();

      return {
        activeLots: lotsResult.data.length,
        totalVolume: Math.round(totalVolume),
        varietals,
      };
    },
    enabled: !!clientId,
  });
}

export function useCreateClient() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateClientInput) => {
      const orgId = await getCurrentUserOrgId();

      const { data, error } = await supabase
        .from("client")
        .insert([{
          first_name: input.first_name || null,
          last_name: input.last_name || null,
          name: input.name,
          code: normalizeClientCode(input.code),
          contact_email: input.contact_email || null,
          phone: input.phone || null,
          org_id: orgId,
        } as any])
        .select()
        .single();

      if (error) throw new Error(clientErrorMessage(error, normalizeClientCode(input.code)));
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: "Client created",
        description: "The client has been successfully created.",
      });

      // Track client creation
      trackEvent({
        name: 'client_created',
        properties: {
          has_contact_info: !!variables.contact_email,
          has_phone: !!variables.phone
        }
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating client",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateClient() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateClientInput }) => {
      const code = input.code !== undefined ? normalizeClientCode(input.code) : undefined;
      const { data, error } = await supabase
        .from("client")
        .update(code !== undefined ? { ...input, code } : input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(clientErrorMessage(error, code));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      toast({
        title: "Client updated",
        description: "The client has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating client",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteClient() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["archived-clients"] });
      toast({
        title: "Client deleted",
        description: "The client has been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      // Check if it's a foreign key constraint error
      const isForeignKeyError = error.message.includes("foreign key constraint") ||
        error.message.includes("violates foreign key");

      toast({
        title: "Cannot delete client",
        description: isForeignKeyError
          ? "This client has associated work orders, wine lots, or other records. Archive the client instead to preserve historical data."
          : error.message,
        variant: "destructive",
      });
    },
  });
}

// Clients that are internal/planning-only and don't need Dropbox folder mapping
export const INTERNAL_CLIENT_NAMES = ["On Deck"];

/**
 * Check if a client is internal/planning-only
 */
export function isInternalClient(clientName: string): boolean {
  return INTERNAL_CLIENT_NAMES.includes(clientName);
}

export interface ClientsMissingDropboxFolder {
  count: number;
  clients: { id: string; name: string; code: string | null }[];
}

/**
 * For each given facility, the count and list of clients missing a Dropbox
 * folder mapping there. Excludes internal/planning-only clients like "On
 * Deck". A client with no business at a facility simply never gets flagged
 * once a mapping is set for it — this is per-facility, not "has any folder
 * at all". One query regardless of how many facilities are passed (not one
 * per facility), so this scales to any number of Documents-enabled
 * facilities without extra round-trips.
 */
export function useClientsMissingDropboxFolders(facilityIds: string[]) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const sortedIds = [...facilityIds].sort();

  return useQuery({
    queryKey: ["clients-missing-dropbox", sortedIds],
    queryFn: async () => {
      const [{ data: allClients, error: clientsError }, { data: mapped, error: mappedError }] = await Promise.all([
        supabase.from("client").select("id, name, code").neq("is_active", false).order("name", { ascending: true }),
        supabase.from("client_facility_folder").select("client_id, facility_id").in("facility_id", sortedIds),
      ]);

      if (clientsError) throw clientsError;
      if (mappedError) throw mappedError;

      const mappedByFacility = new Map<string, Set<string>>();
      for (const m of mapped || []) {
        if (!mappedByFacility.has(m.facility_id)) mappedByFacility.set(m.facility_id, new Set());
        mappedByFacility.get(m.facility_id)!.add(m.client_id);
      }

      const activeClients = (allClients || []).filter((c) => !isInternalClient(c.name));
      const result = new Map<string, ClientsMissingDropboxFolder>();
      for (const facilityId of sortedIds) {
        const mappedIds = mappedByFacility.get(facilityId) ?? new Set<string>();
        const clients = activeClients.filter((c) => !mappedIds.has(c.id));
        result.set(facilityId, { count: clients.length, clients });
      }
      return result;
    },
    enabled: isAuthenticated && !authLoading && sortedIds.length > 0,
  });
}

/**
 * Get a client's Dropbox folder mappings across all facilities.
 */
export function useClientFacilityFolders(clientId: string | undefined) {
  return useQuery({
    queryKey: ["client-facility-folders", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_facility_folder")
        .select("*")
        .eq("client_id", clientId!);

      if (error) throw error;
      return data as ClientFacilityFolder[];
    },
    enabled: !!clientId,
  });
}

/**
 * Set (create or replace) a client's Dropbox folder mapping for one facility.
 */
export function useUpsertClientFacilityFolder() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, facilityId, dropboxFolder }: { clientId: string; facilityId: string; dropboxFolder: string }) => {
      const orgId = await getCurrentUserOrgId();

      const { data, error } = await supabase
        .from("client_facility_folder")
        .upsert(
          { client_id: clientId, facility_id: facilityId, dropbox_folder: dropboxFolder, org_id: orgId },
          { onConflict: "client_id,facility_id" }
        )
        .select()
        .single();

      if (error) throw error;
      return data as ClientFacilityFolder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients-missing-dropbox"] });
      queryClient.invalidateQueries({ queryKey: ["client-facility-folders"] });
      toast({
        title: "Dropbox folder mapped",
        description: "The client's Dropbox folder has been set.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error setting Dropbox folder",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Facilities whose tanks a client can see on the customer-portal Tank Schedule
 * (client_portal_facility). Admin-managed; most clients aren't at every facility.
 */
export function useClientPortalFacilities(clientId: string | undefined) {
  return useQuery({
    queryKey: ["client-portal-facilities", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_portal_facility")
        .select("facility_id")
        .eq("client_id", clientId!);

      if (error) throw error;
      return (data ?? []).map((row) => row.facility_id);
    },
    enabled: !!clientId,
  });
}

/**
 * Turn one facility on or off for a client's portal Tank Schedule.
 */
export function useSetClientPortalFacility() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, facilityId, visible }: { clientId: string; facilityId: string; visible: boolean }) => {
      if (visible) {
        const orgId = await getCurrentUserOrgId();
        const { error } = await supabase
          .from("client_portal_facility")
          .upsert(
            { client_id: clientId, facility_id: facilityId, org_id: orgId },
            { onConflict: "client_id,facility_id", ignoreDuplicates: true }
          );
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("client_portal_facility")
          .delete()
          .eq("client_id", clientId)
          .eq("facility_id", facilityId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-portal-facilities"] });
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: ["client-portal-facilities"] });
      toast({
        title: "Error updating portal access",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export interface AutoMatchResult {
  clientId: string;
  clientName: string;
  folderName: string | null;
  matched: boolean;
}

export interface BatchAutoMatchResults {
  matches: AutoMatchResult[];
  matched: number;
  total: number;
  unmatched: number;
}

/**
 * Batch auto-match Dropbox folders for all clients missing a mapping at one facility
 */
export function useBatchAutoMatchDropboxFolders() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      clients: { id: string; name: string; code: string | null }[];
      folders: { name: string; path: string }[];
      facilityId: string;
      dropboxFolderPrefix: string;
    }): Promise<BatchAutoMatchResults> => {
      const { clients, folders, facilityId, dropboxFolderPrefix } = params;
      const matches: AutoMatchResult[] = [];

      // Find matches for each client
      for (const client of clients) {
        const match = matchClientDropboxFolder(client, folders, dropboxFolderPrefix);
        matches.push({
          clientId: client.id,
          clientName: client.name,
          folderName: match,
          matched: !!match,
        });
      }

      const orgId = await getCurrentUserOrgId();

      // Apply all updates for matched clients
      for (const result of matches) {
        if (result.matched && result.folderName) {
          const { error } = await supabase
            .from("client_facility_folder")
            .upsert(
              { client_id: result.clientId, facility_id: facilityId, dropbox_folder: result.folderName, org_id: orgId },
              { onConflict: "client_id,facility_id" }
            );

          if (error) throw error;
        }
      }

      const matchedCount = matches.filter(m => m.matched).length;

      return {
        matches,
        matched: matchedCount,
        total: clients.length,
        unmatched: clients.length - matchedCount,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients-missing-dropbox"] });
      queryClient.invalidateQueries({ queryKey: ["client-facility-folders"] });
      // Don't show toast here - let the caller handle the results dialog
    },
    onError: (error: Error) => {
      toast({
        title: "Error during auto-match",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Archive a client (soft delete)
 * Sets is_active = false. Action is automatically logged via audit trigger.
 */
export function useArchiveClient() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { data, error } = await supabase
        .from("client")
        .update({ is_active: false })
        .eq("id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["archived-clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["clients-missing-dropbox"] });
      toast({
        title: "Client archived",
        description: `${data.name} has been archived and will no longer appear in active lists.`,
      });

      trackEvent({
        name: 'client_archived',
        properties: { client_id: data.id }
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error archiving client",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Restore an archived client
 * Sets is_active = true. Action is automatically logged via audit trigger.
 */
export function useUnarchiveClient() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { data, error } = await supabase
        .from("client")
        .update({ is_active: true })
        .eq("id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["archived-clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      toast({
        title: "Client restored",
        description: `${data.name} has been restored and is now active.`,
      });

      trackEvent({
        name: 'client_unarchived',
        properties: { client_id: data.id }
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error restoring client",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Fetch archived (inactive) clients
 * Only admins and cellar managers can see archived clients (enforced via RLS)
 */
export function useArchivedClients(searchTerm?: string) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["archived-clients", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("client")
        .select("*")
        .eq("is_active", false)
        .order("name", { ascending: true });

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Client[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}
